import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import PhoneFrame from './phone_frame';

// Langues pour lesquelles on dispose de captures iOS (voir copy-ios-screenshots.js)
const SUPPORTED = ['fr', 'en', 'es', 'it', 'de'];
const FALLBACK_LANG = 'fr';
// Nombre de captures par défaut si le manifest n'est pas (encore) chargé.
const DEFAULT_COUNT = 8;

// Vidéo promo (public/videos/) : française pour fr, anglaise pour toutes les
// autres langues. Fond crème de la vidéo, pour que les bandes du letterbox
// se confondent avec elle.
const VIDEO_BG = '#fdf9e8';
// Vue une fois dans la session → on ne la relance pas à chaque retour sur
// l'accueil, on va droit au carrousel (le bouton « Revoir » reste là).
const VIDEO_SEEN_KEY = 'bible4me.promoVideoSeen';

type Manifest = Record<string, number>;

const videoAlreadySeen = (): boolean => {
  // Navigateur piloté (tests Playwright) : droit au carrousel. WebKit y joue
  // la vidéo, et le son sortait des haut-parleurs à chaque passe de tests.
  if (typeof navigator !== 'undefined' && navigator.webdriver) return true;
  try {
    if (sessionStorage.getItem(VIDEO_SEEN_KEY)) return true;
  } catch {
    /* stockage bloqué : on joue la vidéo */
  }
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
};

const markVideoSeen = () => {
  try {
    sessionStorage.setItem(VIDEO_SEEN_KEY, '1');
  } catch {
    /* sans importance */
  }
};

// Le manifest est généré au build par copy-ios-screenshots.js et donne le
// nombre réel de captures par langue → le carrousel s'adapte automatiquement
// si une publication régénère plus (ou moins) de captures.
const HeroCarousel: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = SUPPORTED.includes(i18n.language) ? i18n.language : FALLBACK_LANG;
  const videoLang = i18n.language === 'fr' ? 'fr' : 'en';

  const [manifest, setManifest] = useState<Manifest | null>(null);
  // La vidéo d'abord, le carrousel ensuite : il ne monte qu'à la fin de la
  // vidéo, donc son autoplay démarre à ce moment-là, sur la première capture.
  const [showVideo, setShowVideo] = useState(() => !videoAlreadySeen());
  const [muted, setMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/screenshots/ios/manifest.json')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data) setManifest(data as Manifest);
      })
      .catch(() => {
        /* on garde le fallback */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // React ne pose pas l'attribut `muted` dans le DOM : sans lui, Safari iOS
  // refuse l'autoplay. On le force, puis on lance la lecture.
  useEffect(() => {
    const v = videoRef.current;
    if (!showVideo || !v) return;
    v.muted = true;
    v.setAttribute('muted', '');
    void v.play().catch(() => undefined);
  }, [showVideo, videoLang]);

  const count = (manifest && manifest[lang]) || DEFAULT_COUNT;

  const endVideo = () => {
    markVideoSeen();
    setShowVideo(false);
  };

  const replayVideo = () => {
    setMuted(true);
    setShowVideo(true);
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    // Sur mobile, l'autoplay peut avoir été refusé : le geste le débloque.
    if (v.paused) void v.play().catch(() => undefined);
  };

  const buttonClass =
    'absolute z-10 rounded-full bg-black/55 text-white text-xs px-2.5 py-1 backdrop-blur-sm hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400';

  return (
    <div className="flex flex-col items-center gap-2">
      <PhoneFrame
        className="mx-auto"
        style={{ ['--swiper-theme-color' as string]: '#78350f', ['--swiper-navigation-size' as string]: '26px' }}
      >
        {showVideo ? (
          <div className="relative w-full h-full" style={{ backgroundColor: VIDEO_BG }}>
            {/* L'autoplay n'est permis par les navigateurs que muet : le son
                s'active d'un appui sur le bouton. */}
            <video
              ref={videoRef}
              key={videoLang}
              src={`/videos/promo_${videoLang}.mp4`}
              poster={`/videos/promo_${videoLang}.jpg`}
              autoPlay
              muted
              playsInline
              preload="auto"
              onEnded={endVideo}
              onError={endVideo}
              className="w-full h-full object-contain"
            />
            <button type="button" onClick={toggleMute} className={`${buttonClass} bottom-3 left-3`}>
              {muted ? `🔇 ${t('home.hero.videoUnmute', 'Son')}` : `🔊 ${t('home.hero.videoMute', 'Couper')}`}
            </button>
            <button type="button" onClick={endVideo} className={`${buttonClass} top-3 right-3`}>
              {t('home.hero.videoSkip', 'Passer')} ›
            </button>
          </div>
        ) : (
          <Swiper
            modules={[Autoplay, Navigation, Pagination]}
            autoplay={{ delay: 4000, pauseOnMouseEnter: true, disableOnInteraction: false }}
            navigation
            pagination={{ clickable: true }}
            loop
            className="w-full h-full"
          >
            {Array.from({ length: count }, (_, i) => {
              const num = String(i + 1).padStart(2, '0');
              return (
                <SwiperSlide key={num}>
                  <img
                    src={`/screenshots/ios/${lang}/${num}.png`}
                    alt={`${t('home.hero.screenshotAlt', "Capture d'écran de l'app Bible4Me")} ${i + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        )}
      </PhoneFrame>
      {!showVideo && (
        <button
          type="button"
          onClick={replayVideo}
          className="text-sm text-amber-900 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded"
        >
          ▶ {t('home.hero.videoReplay', 'Voir la vidéo')}
        </button>
      )}
    </div>
  );
};

export default HeroCarousel;
